import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { getUserUnitsByContext } from '@/modules/units'
import { useAuth } from '@/shared/contexts/AuthContext'

interface UseResolveContextUnitResult {
  resolving: boolean
  ready: boolean
}

export function useResolveContextUnit(
  contextKey: string,
): UseResolveContextUnitResult {
  const navigate = useNavigate()
  const { user } = useAuth()

  const {
    currentUnit,
    isLoading: unitLoading,
    setCurrentUnit: switchUnit,
  } = useUnit()

  const [resolving, setResolving] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    const resolve = async () => {
      if (unitLoading) return

      if (currentUnit?.context_key === contextKey) {
        setReady(true)
        return
      }

      setResolving(true)

      try {
        const userId = user?.id

        if (!userId) {
          navigate('/onboarding', { replace: true })
          return
        }

        const units = await getUserUnitsByContext(
          userId,
          contextKey,
        )

        if (cancelled) return

        if (units.length > 0) {
          await switchUnit(units[0].id)
          setReady(true)
        } else {
          navigate('/onboarding', { replace: true })
        }
      } catch (err) {
        console.error(
          `[useResolveContextUnit] Error resolving "${contextKey}" unit:`,
          err,
        )

        navigate('/onboarding', { replace: true })
      } finally {
        if (!cancelled) {
          setResolving(false)
        }
      }
    }

    resolve()

    return () => {
      cancelled = true
    }
  }, [
    contextKey,
    currentUnit,
    unitLoading,
    switchUnit,
    navigate,
    user?.id,
  ])

  return {
    resolving,
    ready,
  }
}
